import moment from 'moment';
import { useState, useEffect } from 'react';
import { BehaviorSubject, Subscription } from 'rxjs';
import { skip, take } from 'rxjs/operators';
import { AccountId, Count, UserId } from 'api/models';
import { Filter, NotificationStatus, Start } from 'api/notifications';
import { Company } from 'stores/companies';
import { fetch } from './fetch';
import {
  Empty,
  Content,
  FetchInfo,
  Refresh,
  NotificationCount,
} from './models';
import { lastSeenAt, seen } from './use-pending';

interface NotificationsStore {
  refresh: Refresh;
  content: Content;
}

export const useNotificationsStore = (
  status: NotificationStatus,
  filter: Filter,
  [, accountId]: AccountId,
  [, userId]: UserId,
  seeing = false,
  selectedCompany?: Company,
): NotificationsStore => {
  const [content, setContent] = useState<Content>(Empty);
  const [force, setForce] = useState(0);
  const refresh = () => queueMicrotask(() => setForce(force + 1));

  // notify the server that the user has seen the notifications
  useEffect(() => {
    if (seeing) {
      seen(moment());
    }
  }, [seeing, force]);

  useEffect(() => {
    const subscriptions: Subscription[] = [];

    // create a new observable to hold and tract the notification data
    const content$ = new BehaviorSubject<Content>(Empty);

    subscriptions.push(content$.pipe(skip(1)).subscribe(setContent));

    // fetch new notifications when the last seen at changes or user clicks fetch more
    fetch(
      accountId,
      userId,
      status,
      filter,
      content$,
      subscriptions,
      FetchInfo(Count(10), Start(), NotificationCount(5)),
      lastSeenAt().pipe(take(1)),
      selectedCompany,
    );

    return () => subscriptions.forEach((s) => s.unsubscribe());
  }, [accountId, userId, status, filter, force, selectedCompany]);

  // When the content$ observable emits a new value, we update the state content.
  // All teh components using this hook will be re-rendered with the new content.
  return { content, refresh };
};
